/**
 * The 396px column beside the table: what the movement the pointer is on actually is.
 *
 * **One prop — the ROW — and it owns everything else.** The screen hands it the movement and the
 * panel decides what a card says; a panel taking twelve props would put those decisions back in the
 * screen, which is where the plan library's inspector started and stopped.
 *
 * ── The identity row has no photograph (owner, 17 September 2026) ───────────────────────────
 * D3. The head is the name, the type's mark and the „Twoje" badge; the still lives in its own card
 * below. The specimen drew the still in the head and the owner moved it before the canon froze.
 *
 * ── Everything it draws is already on the ROW ───────────────────────────────────────────────
 * The name, the type, the muscles, the equipment and the still all arrive with the catalogue, which
 * landed before the first row was painted. So a swap paints a complete panel on its first frame and
 * there is nothing here to wait for — the skeleton beside this file is for the CATALOGUE's own
 * wait, not for a second read.
 */

import { useTranslation } from 'react-i18next';

import hidden from '~/shared/ui/visuallyHidden.module.css';

import { exerciseTypeColour } from '~/entities/exercise-library';
import { ExerciseTypeGlyph } from '~/entities/exercise-library';

import type { ExerciseRow } from '../model/exerciseRow';

import { EquipmentTiles } from './EquipmentTiles';
import { ExerciseStill } from './ExerciseCells';
import { MuscleFigures } from '~/entities/exercise-library';
import styles from './ExercisePreviewPanel.module.css';

export function ExercisePreviewPanel({ row }: { readonly row: ExerciseRow }) {
  const { t } = useTranslation();
  const primary = row.muscles.filter((pill) => !pill.secondary).map((pill) => pill.group);
  const secondary = row.muscles.filter((pill) => pill.secondary).map((pill) => pill.group);

  return (
    // `complementary`, named by the movement it is describing — which is also how the selection is
    // announced, since `role="table"` has no selection to claim.
    <aside className={styles.panel} aria-label={row.name}>
      <div className={styles.identity}>
        <div>
          {/* The panel's own heading. The `h1` is the screen's title, so this is an `h2`. */}
          <h2 className={styles.name}>{row.name}</h2>
          <div className={styles.sub}>
            <span className={styles.typeMark} style={exerciseTypeColour(row.typeKey)}>
              <ExerciseTypeGlyph type={row.typeKey} className={styles.typeGlyph} />
              {row.typeLabel}
            </span>
            {row.author === 'own' && (
              // The mobile locale's own word, read directly — `exerciseLibrary.list.yourBadge`.
              <span className={styles.badge}>{t('exerciseLibrary.list.yourBadge')}</span>
            )}
          </div>
        </div>
      </div>

      <section className={`${String(styles.card)} ${String(styles.stillCard)}`}>
        {/* The same frame the table's cell draws, at this column's geometry — see
          * `exerciseStill.module.css`. Eager, because the trainer is already looking at it. */}
        <ExerciseStill row={row} className={String(styles.still)} strokeWidth={1.4} eager />
      </section>

      <section className={`${String(styles.card)} ${String(styles.muscleCard)}`}>
        <h3 className={styles.eyebrow}>{t('web:exerciseLibrary.preview.muscles')}</h3>
        {row.muscles.length === 0 ? (
          // A body with nothing lit reads as a render that failed, so it is not drawn at all — the
          // plan library's own rule for a plan whose split the catalogue cannot name.
          <p className={styles.empty}>{t('web:exerciseLibrary.preview.noMuscles')}</p>
        ) : (
          <>
            <div className={styles.figures}>
              <MuscleFigures primary={primary} secondary={secondary} />
            </div>
            <div className={styles.pills}>
              {/* The fill is the only thing telling a primary pill from a secondary one, and a
                * listener cannot hear a fill — so each carries the phone's own word beside it. The
                * theatre's rail prints the same pair as visible row labels. Nothing changes on
                * screen. */}
              {row.muscles.map((pill) => (
                <span
                  key={pill.group}
                  className={`${String(styles.pill)} ${
                    pill.secondary ? String(styles.pillSecondary) : ''
                  }`}
                >
                  {pill.label}
                  <span className={hidden.srOnly}>
                    {' '}
                    {t(
                      pill.secondary
                        ? 'exerciseLibrary.detail.musclesSecondary'
                        : 'exerciseLibrary.detail.musclesPrimary',
                    )}
                  </span>
                </span>
              ))}
            </div>
          </>
        )}
      </section>

      <section className={styles.card}>
        <h3 className={styles.eyebrow}>{t('web:exerciseLibrary.preview.equipment')}</h3>
        <EquipmentTiles pieces={row.equipment} />
      </section>
    </aside>
  );
}
